import { ANALYTICS_EVENTS, serializeAnalyticsError, trackEvent } from "./mixpanel";
import type {
  AuthFailureStep,
  CtaId,
  CtaLocation,
  KitSelectionConfirmationAction,
  MfaAction,
  Outcome,
  PaymentType,
  ResultsReviewDecision,
  ResultsSlideSet,
  SurveyAbandonAction,
  SurveyBlocker,
  Workflow,
  WorkflowStep
} from "./types";

function baseWorkflowProps(
  workflow: Workflow,
  workflowStep: WorkflowStep | AuthFailureStep,
  extra?: Record<string, string | number | boolean | string[] | undefined>
) {
  return {
    workflow,
    workflow_step: workflowStep,
    ...extra
  };
}

// --- Auth ---

export function trackAuthCompleted(props?: {
  onboardingPath?: "standard" | "direct_invitee_unpaid" | "direct_invitee_paid";
}): void {
  trackEvent(ANALYTICS_EVENTS.AUTH_COMPLETED, {
    ...baseWorkflowProps("auth", "completed"),
    outcome: "success",
    onboarding_path: props?.onboardingPath
  });
}

export function trackAuthFailed(
  step: AuthFailureStep,
  error?: unknown,
  props?: {
    failureReason?: string;
    httpStatus?: number;
  }
): void {
  trackEvent(ANALYTICS_EVENTS.AUTH_FAILED, {
    ...baseWorkflowProps("auth", step),
    outcome: "failure",
    error_type: error ? serializeAnalyticsError(error) : undefined,
    failure_reason: props?.failureReason,
    http_status: props?.httpStatus
  });
}

// --- Dashboard CTAs (workflow entry points) ---

export function trackDashboardCtaClicked(props: {
  workflow: Workflow;
  ctaLocation: CtaLocation;
  ctaId: CtaId;
}): void {
  trackEvent(ANALYTICS_EVENTS.DASHBOARD_CTA_CLICKED, {
    ...baseWorkflowProps(props.workflow, "cta_clicked"),
    cta_location: props.ctaLocation,
    cta_id: props.ctaId
  });
}

// --- Surveys ---

export function trackSurveyStarted(surveyId: string, workflow: Workflow): void {
  trackEvent(ANALYTICS_EVENTS.SURVEY_STARTED, {
    ...baseWorkflowProps(workflow, "started"),
    survey_id: surveyId
  });
}

export function trackSurveyCompleted(props: {
  surveyId: string;
  workflow: Workflow;
  outcome: "completed" | "blocked";
  blockers?: SurveyBlocker[];
}): void {
  trackEvent(ANALYTICS_EVENTS.SURVEY_COMPLETED, {
    ...baseWorkflowProps(props.workflow, "completed"),
    survey_id: props.surveyId,
    outcome: props.outcome,
    blockers: props.blockers
  });
}

export function trackSurveySaveFailed(
  surveyId: string,
  workflow: Workflow,
  error?: unknown
): void {
  trackEvent(ANALYTICS_EVENTS.SURVEY_SAVE_FAILED, {
    ...baseWorkflowProps(workflow, "submitted"),
    survey_id: surveyId,
    outcome: "failure",
    error_type: serializeAnalyticsError(error)
  });
}

export function trackSurveyParseFailed(surveyId: string, workflow: Workflow): void {
  trackEvent(ANALYTICS_EVENTS.SURVEY_PARSE_FAILED, {
    ...baseWorkflowProps(workflow, "submitted"),
    survey_id: surveyId,
    outcome: "failure"
  });
}

export function trackSurveyAbandonAttempted(props: {
  surveyId: string;
  workflow: Workflow;
  action: SurveyAbandonAction;
}): void {
  trackEvent(ANALYTICS_EVENTS.SURVEY_ABANDON_ATTEMPTED, {
    ...baseWorkflowProps(props.workflow, "abandon_attempted"),
    survey_id: props.surveyId,
    action: props.action
  });
}

// --- Kit order ---

export function trackKitOrderWorkflowStarted(props: {
  kitUuids: string[];
}): void {
  trackEvent(ANALYTICS_EVENTS.KIT_ORDER_WORKFLOW_STARTED, {
    ...baseWorkflowProps("kit_order", "started"),
    kit_uuids: props.kitUuids,
    kit_count: props.kitUuids.length
  });
}

export function trackKitSelectionConfirmationResolved(props: {
  action: KitSelectionConfirmationAction;
  suggestedTests: string[];
}): void {
  trackEvent(ANALYTICS_EVENTS.KIT_SELECTION_CONFIRMATION_RESOLVED, {
    ...baseWorkflowProps("kit_order", "decision"),
    action: props.action,
    suggested_tests: props.suggestedTests
  });
}

export function trackKitOrderSubmitted(props: {
  kitUuid: string;
  outcome: Outcome;
  httpStatus?: number;
  errorType?: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.KIT_ORDER_SUBMITTED, {
    ...baseWorkflowProps("kit_order", "submitted"),
    kit_uuid: props.kitUuid,
    outcome: props.outcome,
    http_status: props.httpStatus,
    error_type: props.errorType
  });
}

export function trackKitOrderBatchCompleted(props: {
  outcome: Outcome;
  kitUuidsOrdered: string[];
  kitUuidsFailed: string[];
}): void {
  trackEvent(ANALYTICS_EVENTS.KIT_ORDER_BATCH_COMPLETED, {
    ...baseWorkflowProps("kit_order", "completed"),
    outcome: props.outcome,
    kit_uuids_ordered: props.kitUuidsOrdered,
    kit_uuids_failed: props.kitUuidsFailed
  });
}

// --- Forms ---

export function trackFormSubmissionCompleted(props: {
  formId: string;
  formVersion: string;
  outcome: Outcome;
  errorType?: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.FORM_SUBMISSION_COMPLETED, {
    ...baseWorkflowProps("form_submission", "submitted"),
    form_id: props.formId,
    form_version: props.formVersion,
    outcome: props.outcome,
    error_type: props.errorType
  });
}

// --- Results review ---

export function trackResultsReviewStarted(slideSet: ResultsSlideSet): void {
  trackEvent(ANALYTICS_EVENTS.RESULTS_REVIEW_STARTED, {
    ...baseWorkflowProps("results_review", "started"),
    slide_set: slideSet
  });
}

export function trackResultsReviewCompleted(props: {
  slideSet: ResultsSlideSet;
  slidesViewedCount: number;
}): void {
  trackEvent(ANALYTICS_EVENTS.RESULTS_REVIEW_COMPLETED, {
    ...baseWorkflowProps("results_review", "completed"),
    slide_set: props.slideSet,
    slides_viewed_count: props.slidesViewedCount,
    outcome: "success"
  });
}

export function trackResultsReviewDecision(decision: ResultsReviewDecision): void {
  trackEvent(ANALYTICS_EVENTS.RESULTS_REVIEW_DECISION, {
    ...baseWorkflowProps("results_review", "decision"),
    decision,
    outcome: decision === "defer" ? "deferred" : "success"
  });
}

export function trackResultsDownloadCompleted(props: {
  documentId: string;
  orderId?: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.RESULTS_DOWNLOAD_COMPLETED, {
    ...baseWorkflowProps("results_review", "completed"),
    document_id: props.documentId,
    order_id: props.orderId,
    outcome: "success"
  });
}

export function trackResultsDownloadFailed(props: {
  documentId: string;
  orderId?: string;
  error?: unknown;
}): void {
  trackEvent(ANALYTICS_EVENTS.RESULTS_DOWNLOAD_FAILED, {
    ...baseWorkflowProps("results_review", "failed"),
    document_id: props.documentId,
    order_id: props.orderId,
    outcome: "failure",
    error_type: serializeAnalyticsError(props.error)
  });
}

// --- Account ---

export function trackAccountMfaCompleted(action: MfaAction): void {
  trackEvent(ANALYTICS_EVENTS.ACCOUNT_MFA_COMPLETED, {
    ...baseWorkflowProps("account", "completed"),
    action,
    outcome: "success"
  });
}

export function trackAccountMfaFailed(action: MfaAction, error?: unknown): void {
  trackEvent(ANALYTICS_EVENTS.ACCOUNT_MFA_FAILED, {
    ...baseWorkflowProps("account", "failed"),
    action,
    outcome: "failure",
    error_type: serializeAnalyticsError(error)
  });
}

export function trackPasswordResetRequested(outcome: Outcome, error?: unknown): void {
  trackEvent(ANALYTICS_EVENTS.PASSWORD_RESET_REQUESTED, {
    ...baseWorkflowProps("account", "submitted"),
    outcome,
    error_type: error ? serializeAnalyticsError(error) : undefined
  });
}

function paymentWorkflow(paymentType: PaymentType): Workflow {
  return paymentType === "unpaid_invite" ? "onboarding" : "account";
}

export function trackPaymentCompleted(props: {
  paymentType: PaymentType;
}): void {
  trackEvent(ANALYTICS_EVENTS.PAYMENT_COMPLETED, {
    ...baseWorkflowProps(paymentWorkflow(props.paymentType), "completed"),
    payment_type: props.paymentType,
    outcome: "success"
  });
}

export function trackPaymentFailed(props: {
  paymentType: PaymentType;
  failureReason: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.PAYMENT_FAILED, {
    ...baseWorkflowProps(paymentWorkflow(props.paymentType), "failed"),
    payment_type: props.paymentType,
    outcome: "failure",
    failure_reason: props.failureReason
  });
}

// --- Spouse invite ---

export function trackSpouseInviteCompleted(): void {
  trackEvent(ANALYTICS_EVENTS.SPOUSE_INVITE_COMPLETED, {
    ...baseWorkflowProps("spouse_invite", "completed"),
    outcome: "success"
  });
}

export function trackSpouseInviteFailed(error?: unknown): void {
  trackEvent(ANALYTICS_EVENTS.SPOUSE_INVITE_FAILED, {
    ...baseWorkflowProps("spouse_invite", "failed"),
    outcome: "failure",
    error_type: serializeAnalyticsError(error)
  });
}
